import { Box, Flex, useUiKitComponents } from "@qws-org/ui-kit";
import type { FC } from "react";
import { useState } from "react";

import type { Action, HeaderSdk, LayoutV2, Pages } from "~/core";
import type { GetSettingsQuery as GetSettingsQuerySdk } from "~/core/sdk";
import { BottomHeaderMenuList } from "~/features/headerV4/components/BottomHeaderMenuList";
import { BurgerMenu } from "~/features/headerV4/components/BurgerMenu";
import { HeaderLogo } from "~/features/headerV4/components/HeaderLogo";
import { MobileMenuList } from "~/features/headerV4/components/MobileMenuList";
import { TopHeaderComponent } from "~/features/headerV4/components/TopHeaderComponent";
import { isButtonVariant } from "~/features/headerV4/isButtonVariant";
import type { LocaleAlternate } from "~/shared/utils/locale-navigation";

import { HeaderButton } from "./components/HeaderButton";
import { LocaleSelect } from "./components/LocaleSelect";

export type HeaderData = LayoutV2 & {
  language: string;
  availableLanguages: Array<string>;
  languages: GetSettingsQuerySdk["languages"];
  hreflangAlternates?: Array<LocaleAlternate>;
};

export type HeaderProps = {
  data: HeaderData;
  clickHeaderMain: (targetUrl: string) => void;
  clickHeaderSub: (targetUrl: string) => void;
  onCallAction: (action: Action) => void;
  isActionInactive: (control: Action) => boolean;
  getUrl: (page: Pages) => string;
};

export type MenuItemType = NonNullable<
  ExtractArrayItemType<
    NonNullable<NonNullable<NonNullable<HeaderSdk>["menu"]>["action_items"]>
  >
>["menu_action_items_id"];

export const HeaderV4: FC<HeaderProps> = (props) => {
  const {
    data,
    clickHeaderMain,
    clickHeaderSub,
    onCallAction,
    isActionInactive,
    getUrl,
  } = props;

  const { Link } = useUiKitComponents();
  const [isBurgerMenuOpen, setBurgerMenuOpen] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<string>("");

  const onNavlinkClick = (url: string, id: string): void => {
    clickHeaderMain(url);
    setCurrentPage(id);
  };

  const onBurgerMenuClick = (): void => setBurgerMenuOpen(!isBurgerMenuOpen);

  const closeBurgerMenu = (): void => setBurgerMenuOpen(false);

  return (
    <Box
      position={{ zIndex: 50, type: "sticky", top: 0 }}
      as="header"
      data-testid="header"
    >
      {data.top_menu && <TopHeaderComponent {...props} />}

      <Flex
        align="center"
        display="flex"
        justify="space-between"
        p={{ px: 16, py: 10 }}
        md={{
          p: { px: 40, py: 16 },
          m: { mr: 0 },
          maxWidth: "100%",
        }}
        d={{ minHeight: 116, p: { py: 24 } }}
        minWidth="none"
        bg="background-primary"
      >
        {data?.header_logo && (
          <Link
            onClick={() => {
              closeBurgerMenu();
              onNavlinkClick("/", "");
            }}
            href="/"
            data-testid="header-main-page-link"
          >
            <HeaderLogo data={data} d={{ m: { mr: 40 } }} />
          </Link>
        )}
        {/* десктоп меню  */}
        <BottomHeaderMenuList
          data={data}
          clickHeaderMain={clickHeaderMain}
          clickHeaderSub={clickHeaderSub}
          onCallAction={onCallAction}
          isActionInactive={isActionInactive}
          getUrl={getUrl}
          currentPage={currentPage}
          onNavlinkClick={onNavlinkClick}
          closeBurgerMenu={closeBurgerMenu}
        />

        {/* мобильное меню */}
        {data.mobile_burger_menu && isBurgerMenuOpen && (
          <MobileMenuList
            data={data}
            clickHeaderMain={clickHeaderMain}
            clickHeaderSub={clickHeaderSub}
            onCallAction={onCallAction}
            isActionInactive={isActionInactive}
            getUrl={getUrl}
            closeBurgerMenu={closeBurgerMenu}
            isBurgerMenuOpen={isBurgerMenuOpen}
          />
        )}
        <Flex className="flex-1 basis-0  " />
        {data?.languages &&
          data.language_selector_position === "bottom-menu" && (
            <Flex
              align="center"
              p={{ px: 10 }}
              m={{ mr: 12 }}
              position={{ type: "relative" }}
              display="hidden"
              width="100%"
              md={{ display: "flex", width: "auto", m: { mr: 12 } }}
            >
              <LocaleSelect
                {...data}
                isNonModal={false}
                languages={data.languages}
                variant="positioned"
                offset={25}
              />
            </Flex>
          )}
        {data?.additional_menu && (
          <Flex
            align="center"
            gap={10}
            minWidth="fit-content"
            maxWidth="fit-content"
          >
            {data.additional_menu.action_items
              ?.filter((action) => {
                if (action?.menu_action_items_id?.action) {
                  return !isActionInactive(action.menu_action_items_id.action);
                }
                return true;
              })
              ?.map((item, idx) => {
                const variant = isButtonVariant(
                  item?.menu_action_items_id?.variant,
                )
                  ? item?.menu_action_items_id?.variant
                  : "default";

                return (
                  <HeaderButton
                    key={item?.id}
                    size="xsmall"
                    md={{ fontSize: "body.desktop.medium" }}
                    weight="bold"
                    item={item?.menu_action_items_id}
                    getUrl={getUrl}
                    onCallAction={onCallAction}
                    data-testid={`header-button-${idx}`}
                    variant={variant}
                  />
                );
              })}
          </Flex>
        )}

        <BurgerMenu
          onBurgerMenuClick={onBurgerMenuClick}
          isBurgerMenuOpen={isBurgerMenuOpen}
        />
      </Flex>
    </Box>
  );
};
